import React, { useCallback, useEffect, useState } from 'react';
import { func, number } from 'prop-types';
import { getMapZsg } from './map-zsg';

import { StyledOverlay, StyledStaticMap } from './styles';

/**
 StaticMap is used to render a static map image using Google Maps.
 It will center the map to the latitude (lat) and longitude (lng) provided.

 The url created needs to be signed using the proper API Key. Since this is done with
 an API call, we use a promise prop called signUrl to pass the url to sign. It is up to the
 consumer to generate the proper signed URL.

 signUrl(url, brand) needs to return a promise that returns a signedUrl.

 Here is a semi-pseudocode example of how someone might implement StaticMap:
 const signUrl = (url, brand) => dispatch(Action.signZsgUrl(url));
 <StaticMap signUrl={signUrl} lat={property.lat} lng={property.lng} width="1024" height="400" />

 */

const propTypes = {
  height: number,
  lat: number.isRequired,
  lng: number.isRequired,
  signUrl: func.isRequired,
  width: number,
};

const defaultProps = {
  height: 2048,
  width: 2048,
};

interface StaticMapProps {
  height: number;
  lat: number;
  lng: number;
  signUrl: (url: string, brand: string) => Promise<string>;
  width: number;
}

const StaticMap = ({ height, lat, lng, signUrl, width }: StaticMapProps) => {
  const [url, setUrl] = useState('');
  const searchKey = /key=/;

  const getBackgroundUrl = () => {
    if (url) {
      if (url.search(searchKey) < 0) {
        return `url(${url}key=AIzaSyCvrPLATBdB9qCbSk1qVS_3s0exFoG8feU&)`;
      } else {
        return `url(${url})`;
      }
    }
  };

  const buildUrl = useCallback(() => {
    const map = getMapZsg({ lat, lng, width, scale: 2, height, zoom: 17 });

    return signUrl(map.url, 'zillow').then((url) => setUrl(url));
  }, [height, lat, lng, signUrl, width]);

  useEffect(() => {
    buildUrl();
  }, [buildUrl, height, lat, lng, signUrl, width]);

  return (
    <StyledStaticMap className="grid-map" data-testid="StaticMap" backgroundImage={getBackgroundUrl()}>
      <StyledOverlay />
    </StyledStaticMap>
  );
};

StaticMap.propTypes = propTypes;
StaticMap.defaultProps = defaultProps;

export default StaticMap;
